/**
 * Inställningar › Automatisk tilldelning (molnfast-react#1653): every new
 * felanmälan goes to one förvaltare (an external user) who judges it against
 * the threshold in their guest portal. Saved through the callable, which also
 * ensures the förvaltare a permanent link.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { useAutoAssignmentSettings } from '../../hooks/issues/useAutoAssignmentSettings';
import { toExternalUsers, useUsersData } from '../../Helpers/UsersContext';
import { validateIssueAutoAssignment } from '../../api/autoAssignmentApi';
import { formatThreshold, IssueAutoAssignment } from '../../types/autoAssignment';
import { GuestLinkSummary } from '../../types/guestAccess';
import { formatDateForDisplay } from '../../Helpers/DateUtils';

interface Draft {
    enabled: boolean;
    externalUserId: string;
    threshold: string;
    notifyUids: string[];
}

const draftFrom = (setting: IssueAutoAssignment | null): Draft => ({
    enabled: setting?.enabled ?? false,
    externalUserId: setting?.externalUserId ?? '',
    threshold: setting ? String(setting.threshold) : '',
    notifyUids: Object.keys(setting?.notifyUids ?? {}),
});

const linkText = (link: GuestLinkSummary): string =>
    link.kind === 'permanent'
        ? 'Förvaltaren har en aktiv permanent länk till sin portal.'
        : `Förvaltaren har en aktiv länk som gäller till ${formatDateForDisplay(link.expiresAt)}. Den görs permanent när du sparar.`;

const AutoAssignmentSettings: React.FC = () => {
    const { setting, link, loading, saving, error, save } = useAutoAssignmentSettings();
    const { users, loadingUsers } = useUsersData();
    const [draft, setDraft] = useState<Draft>(draftFrom(null));
    const [formError, setFormError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        setDraft(draftFrom(setting));
    }, [setting]);

    const externalUsers = useMemo(
        () => toExternalUsers(users).sort((a, b) => a.name.localeCompare(b.name, 'sv')),
        [users]
    );
    const internalUsers = useMemo(
        () => (users ?? []).filter((u) => !u.isExternal && !u.deleted).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'sv')),
        [users]
    );
    const unknownSelected = draft.externalUserId !== '' && !externalUsers.some((u) => u.uid === draft.externalUserId);

    const disabled = loading || saving;
    // The link status is the saved förvaltare's; it says nothing about another one picked but not saved.
    const showLinkStatus = !!setting?.externalUserId && setting.externalUserId === draft.externalUserId;
    const linkActive = link?.state === 'active';

    const toggleNotify = (uid: string) => {
        setDraft((d) => ({
            ...d,
            notifyUids: d.notifyUids.includes(uid) ? d.notifyUids.filter((u) => u !== uid) : [...d.notifyUids, uid],
        }));
    };

    const handleSave = async () => {
        setSaved(false);
        const threshold = draft.threshold.trim() === '' ? NaN : Number(draft.threshold);
        const payload = {
            enabled: draft.enabled,
            externalUserId: draft.externalUserId || undefined,
            threshold,
            // A user deleted since the last save is dropped (the callable refuses it).
            notifyUids: draft.notifyUids.filter((uid) => internalUsers.some((u) => u.key === uid)),
        };
        const invalid = validateIssueAutoAssignment(payload);
        if (invalid) {
            setFormError(invalid);
            return;
        }
        setFormError(null);
        try {
            await save(payload);
            setSaved(true);
        } catch {
            // error shown from the hook
        }
    };

    if (loading || loadingUsers) {
        return (
            <div className="d-flex align-items-center gap-2 text-muted">
                <Spinner animation="border" size="sm" /> Laddar…
            </div>
        );
    }

    return (
        <section style={{ maxWidth: 640 }}>
            <h2 className="h5">Felanmälningar</h2>
            <p className="text-muted">
                Varje ny felanmälan skickas till förvaltaren, som bedömer om kostnaden är under eller över
                beloppsgränsen. Under gränsen skapas ett ärende som tilldelas förvaltaren automatiskt. Över gränsen
                ligger felanmälan kvar under Att hantera för styrelsen. Felanmälningar som redan finns berörs inte.
            </p>

            {(formError || error) && <Alert variant="danger">{formError ?? error}</Alert>}
            {saved && <Alert variant="success" dismissible onClose={() => setSaved(false)}>Inställningen är sparad.</Alert>}

            <Form.Check
                type="switch"
                id="auto-assignment-enabled"
                className="mb-3"
                label="Skicka felanmälningar till förvaltare automatiskt"
                checked={draft.enabled}
                disabled={disabled}
                onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
            />

            <Form.Group className="mb-3" controlId="auto-assignment-manager">
                <Form.Label>Förvaltare</Form.Label>
                <Form.Select
                    value={draft.externalUserId}
                    disabled={disabled}
                    onChange={(e) => setDraft((d) => ({ ...d, externalUserId: e.target.value }))}
                >
                    <option value="">Välj extern användare…</option>
                    {unknownSelected && <option value={draft.externalUserId}>Okänd eller borttagen användare</option>}
                    {externalUsers.map((u) => (
                        <option key={u.uid} value={u.uid}>{u.company ? `${u.name} (${u.company})` : u.name}</option>
                    ))}
                </Form.Select>
                {externalUsers.length === 0 && (
                    <Form.Text>Lägg först till förvaltaren som extern användare under Hantera användare.</Form.Text>
                )}
            </Form.Group>

            {draft.enabled && draft.externalUserId && (
                showLinkStatus && linkActive && link ? (
                    <p className="small text-muted">{linkText(link)}</p>
                ) : showLinkStatus && setting?.enabled ? (
                    <Alert variant="warning">
                        Förvaltaren har ingen aktiv länk och kan inte bedöma felanmälningar. Spara igen så skapas en
                        permanent länk.
                    </Alert>
                ) : (
                    <p className="small text-muted">När du sparar får förvaltaren en permanent länk till sin portal.</p>
                )
            )}

            <Form.Group className="mb-3" controlId="auto-assignment-threshold">
                <Form.Label>Beloppsgräns (kr exkl. moms)</Form.Label>
                <InputGroup style={{ maxWidth: 240 }}>
                    <Form.Control
                        type="number"
                        inputMode="numeric"
                        min={1}
                        step={1}
                        placeholder="5000"
                        value={draft.threshold}
                        disabled={disabled}
                        onChange={(e) => setDraft((d) => ({ ...d, threshold: e.target.value }))}
                    />
                    <InputGroup.Text>kr</InputGroup.Text>
                </InputGroup>
                {setting && setting.threshold > 0 && String(setting.threshold) !== draft.threshold.trim() && (
                    <Form.Text>{`Sparad gräns: ${formatThreshold(setting.threshold)} kr. Redan fattade beslut ändras inte.`}</Form.Text>
                )}
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Meddela vid över beloppsgräns</Form.Label>
                <Form.Text as="p" className="mt-0">
                    De valda användarna får e-post när förvaltaren bedömer att en felanmälan är över gränsen.
                </Form.Text>
                {internalUsers.length === 0 ? (
                    <p className="text-muted small mb-0">Inga användare.</p>
                ) : internalUsers.map((u) => (
                    <Form.Check
                        key={u.key}
                        type="checkbox"
                        id={`auto-assignment-notify-${u.key}`}
                        label={u.name || 'Namnlös användare'}
                        checked={draft.notifyUids.includes(u.key)}
                        disabled={disabled}
                        onChange={() => toggleNotify(u.key)}
                    />
                ))}
            </Form.Group>

            <Button variant="primary" onClick={() => { void handleSave(); }} disabled={disabled}>
                {saving ? 'Sparar…' : 'Spara'}
            </Button>
        </section>
    );
};

export default AutoAssignmentSettings;
